import React from 'react'
import { triggerShortcut } from '@helpers'
import {
  FormulaGraphTooltipItem,
  SelectedElementTooltipItem
} from './components'
import { EditorContext } from './EditorContext'
import { Palette } from './Tools'
import { useClassroomPermissions } from '../../contexts'
import { Classroom as CC } from '@constants'

const SelectedElementTooltip = ({ canvas }) => {
  const [coords, setCoords] = React.useState(null)
  const { state, setEditorState } = React.useContext(EditorContext)
  const containerRef = React.useRef()
  const { checkPermission } = useClassroomPermissions()

  const activeObject = canvas?.getActiveObject()

  const containerWidth = containerRef.current
    ? containerRef.current.getBoundingClientRect().width
    : 0

  const duplicate = () => {
    triggerShortcut('CtrlOrCmd+c')
    triggerShortcut('CtrlOrCmd+v')
  }

  const removeObject = () => {
    triggerShortcut('backspace')
  }

  const bringForward = () => {
    if (!activeObject) return
    activeObject.bringForward()
    canvas.renderAll()
  }

  const sendBackwards = () => {
    if (!activeObject) return
    activeObject.sendBackwards()
    canvas.renderAll()
  }

  React.useEffect(() => {
    if (!canvas) return

    const calculateTooltipCoords = ({
      left,
      top,
      height,
      width,
      scaleX,
      scaleY,
      canvas
    }) => {
      const [canvasScaleX, _, __, canvasScaleY, canvasLeft, canvasTop] = // eslint-disable-line
        canvas.viewportTransform

      return {
        left: left * canvasScaleX + canvasLeft,
        top:
          top * canvasScaleY + height * scaleY * canvasScaleY + canvasTop + 10,
        width: width * scaleX * canvasScaleX
      }
    }

    const positionTooltip = (target) => {
      setCoords(calculateTooltipCoords(target))
    }

    const onSelection = (event) => {
      const { selected } = event

      if (selected.length === 1) positionTooltip(selected[0])
    }

    const onSelectionCleared = () => {
      setCoords(null)
    }

    canvas.on('selection:created', onSelection)
    canvas.on('selection:updated', onSelection)
    canvas.on('selection:cleared', onSelectionCleared)
    canvas.on('object:moving', (event) => positionTooltip(event.target))
    canvas.on('object:scaling', (event) => positionTooltip(event.target))
  }, [canvas])

  if (!coords) {
    return null
  }

  const styles = containerWidth
    ? {
        top: coords.top,
        left: coords.left + (coords.width - containerWidth) / 2
      }
    : {}

  return (
    <div style={styles} ref={containerRef} className='element-tooltip'>
      <Palette
        color={state.color}
        onClick={(patch) => setEditorState({ color: patch })}
      />

      {['Formula', 'Graph'].includes(activeObject?.type) && (
        <FormulaGraphTooltipItem />
      )}

      <SelectedElementTooltipItem
        icon='copy'
        tooltip='Duplicate'
        onClick={duplicate}
      />

      <SelectedElementTooltipItem
        icon='bring-forward'
        tooltip='Bring forward'
        onClick={bringForward}
      />

      <SelectedElementTooltipItem
        icon='send-backwards'
        tooltip='Send backwards'
        onClick={sendBackwards}
      />

      {checkPermission(CC.FEATURES.SKETCH_ERASER) ? (
        <SelectedElementTooltipItem
          icon='eraser'
          tooltip='Delete'
          onClick={removeObject}
        />
      ) : null}
    </div>
  )
}

export default SelectedElementTooltip
